(function () {
  'use strict';

  var state = {
    observer: null,
    loading: false,
    generation: 0,
    controller: null,
    sentinel: null,
    sectionAPIAvailable: true
  };

  var selectors = {
    ajaxContent: '#CollectionAjaxContent',
    grid: '.collection-grid__wrapper .grid.grid--uniform',
    pagination: '.pagination',
    next: '.pagination .next a'
  };

  function getRoot() {
    return document.querySelector(selectors.ajaxContent) || document;
  }

  function getGrid(root) {
    return root.querySelector(selectors.grid);
  }

  function getPagination(root) {
    return root.querySelector(selectors.pagination);
  }

  function getNextLink(root) {
    return root.querySelector(selectors.next);
  }

  function removeSentinel() {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    if (state.sentinel && state.sentinel.parentNode) {
      state.sentinel.parentNode.removeChild(state.sentinel);
    }

    state.sentinel = null;
  }

  function stopPendingRequest() {
    if (state.controller) {
      try {
        state.controller.abort();
      } catch (e) {}
      state.controller = null;
    }
    state.loading = false;
  }

  function markImagesVisible(nodes) {
    nodes.forEach(function (node) {
      if (!node || node.nodeType !== 1) return;

      if (node.matches && node.matches('image-element[data-aos]')) {
        node.classList.add('aos-animate');
      }

      node.querySelectorAll('image-element[data-aos]').forEach(function (imageElement) {
        imageElement.classList.add('aos-animate');
      });
    });
  }

  function reinitializeProductCards(nodes) {
    // Collection pages have animations disabled. AOS.refreshHard() walks the
    // whole page and becomes increasingly expensive as more products are
    // appended, so deliberately do not run it here. Initialize only behaviour
    // that the newly appended cards actually need.
    try {
      if (window.theme) {
        nodes.forEach(function (node) {
          if (window.theme.collapsibles && typeof window.theme.collapsibles.init === 'function') {
            window.theme.collapsibles.init(node);
          }
        });
      }
    } catch (e) {}

    document.dispatchEvent(new CustomEvent('collection:products-appended', {
      detail: { count: nodes.length }
    }));
  }

  function updatePagination(currentRoot, nextDoc) {
    var currentPagination = getPagination(currentRoot);
    var nextRoot = nextDoc.querySelector(selectors.ajaxContent) || nextDoc;
    var incomingPagination = getPagination(nextRoot);

    if (currentPagination && incomingPagination) {
      currentPagination.replaceWith(incomingPagination);
      return incomingPagination;
    }

    if (currentPagination && !incomingPagination) {
      currentPagination.remove();
    }

    return incomingPagination;
  }

  // On the large Festive Wear collection, Shopify's Section Rendering API
  // returns ONLY the collection HTML rather than every app, script, header and
  // footer on the full page. Keep the normal URL as a safe fallback.
  function getSectionUrl(nextLink) {
    if (!state.sectionAPIAvailable) return null;
    var section = document.querySelector('.collection-content[data-cc-festive-fast="true"][data-section-id]');
    if (!section) return null;
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return null;
    try {
      var url = new URL(nextLink.href, window.location.href);
      url.searchParams.set('section_id', sectionId);
      return url.toString();
    } catch (error) {
      return null;
    }
  }

  function requestNextGrid(nextLink, options) {
    var sectionUrl = getSectionUrl(nextLink);

    function parseResponse(response) {
      if (!response.ok) throw new Error('Unable to load next collection page');
      return response.text().then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var root = doc.querySelector(selectors.ajaxContent) || doc;
        if (!getGrid(root)) throw new Error('Missing next product grid');
        return doc;
      });
    }

    if (!sectionUrl) return fetch(nextLink.href, options).then(parseResponse);

    return fetch(sectionUrl, options).then(parseResponse).catch(function (error) {
      if (error && error.name === 'AbortError') throw error;
      // Shopify may reject section requests on customized/legacy templates.
      // Never let this leave the customer stuck: retry the original paginated URL.
      state.sectionAPIAvailable = false;
      return fetch(nextLink.href, options).then(parseResponse);
    });
  }

  function showComplete() {
    removeSentinel();
    document.documentElement.classList.remove('cc-infinite-scroll-active');
  }

  function loadNextPage(generation) {
    if (state.loading || generation !== state.generation) return;

    var root = getRoot();
    var nextLink = getNextLink(root);
    var currentGrid = getGrid(root);

    if (!nextLink || !currentGrid) {
      showComplete();
      return;
    }

    state.loading = true;
    if (state.sentinel) {
      state.sentinel.classList.add('is-loading');
      state.sentinel.setAttribute('aria-busy', 'true');
    }

    state.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    var fetchOptions = {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    };

    if (state.controller) {
      fetchOptions.signal = state.controller.signal;
    }

    requestNextGrid(nextLink, fetchOptions)
      .then(function (nextDoc) {
        if (generation !== state.generation) return;

        var nextRoot = nextDoc.querySelector(selectors.ajaxContent) || nextDoc;
        var nextGrid = getGrid(nextRoot);

        if (!nextGrid) {
          showComplete();
          return;
        }

        var newNodes = Array.prototype.slice.call(nextGrid.children);
        if (!newNodes.length) {
          showComplete();
          return;
        }

        var fragment = document.createDocumentFragment();
        newNodes.forEach(function (node) {
          fragment.appendChild(document.importNode(node, true));
        });

        var beforeCount = currentGrid.children.length;
        currentGrid.appendChild(fragment);
        var appended = Array.prototype.slice.call(currentGrid.children, beforeCount);

        // Lazy images in this theme start at opacity:0 until AOS marks their
        // image-element wrapper as animated. Mark just-appended products now so
        // they are visible immediately, while the images themselves remain lazy.
        markImagesVisible(appended);

        updatePagination(root, nextDoc);
        reinitializeProductCards(appended);

        state.loading = false;
        state.controller = null;

        // Rebuild the observer from the newly returned pagination link.
        window.requestAnimationFrame(function () {
          init(false);
        });
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;

        state.loading = false;
        state.controller = null;

        if (state.sentinel) {
          state.sentinel.classList.remove('is-loading');
          state.sentinel.classList.add('has-error');
          state.sentinel.setAttribute('aria-busy', 'false');
          state.sentinel.innerHTML = '<button type="button" class="cc-infinite-scroll__retry">Load more products</button>';
          var retry = state.sentinel.querySelector('.cc-infinite-scroll__retry');
          if (retry) {
            retry.addEventListener('click', function () {
              state.sentinel.classList.remove('has-error');
              state.sentinel.innerHTML = '<span class="cc-infinite-scroll__spinner" aria-hidden="true"></span><span class="cc-infinite-scroll__text">Loading more products…</span>';
              loadNextPage(generation);
            }, { once: true });
          }
        }
      });
  }

  function init(resetRequest) {
    state.generation += 1;
    var generation = state.generation;

    removeSentinel();
    if (resetRequest !== false) {
      stopPendingRequest();
    }

    var root = getRoot();
    var grid = getGrid(root);
    var pagination = getPagination(root);
    var nextLink = getNextLink(root);

    if (!grid || !pagination || !nextLink || !('IntersectionObserver' in window)) {
      document.documentElement.classList.remove('cc-infinite-scroll-active');
      return;
    }

    document.documentElement.classList.add('cc-infinite-scroll-active');

    var sentinel = document.createElement('div');
    sentinel.className = 'cc-infinite-scroll';
    sentinel.setAttribute('role', 'status');
    sentinel.setAttribute('aria-live', 'polite');
    sentinel.setAttribute('aria-busy', 'false');
    sentinel.innerHTML = '<span class="cc-infinite-scroll__spinner" aria-hidden="true"></span><span class="cc-infinite-scroll__text">Loading more products…</span>';

    pagination.insertAdjacentElement('afterend', sentinel);
    state.sentinel = sentinel;

    state.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          loadNextPage(generation);
        }
      });
    }, {
      root: null,
      rootMargin: document.querySelector('.collection-content[data-cc-festive-fast="true"]')
        ? '100px 0px 100px 0px'
        : '120px 0px 120px 0px',
      threshold: 0.01
    });

    state.observer.observe(sentinel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(true); }, { once: true });
  } else {
    init(true);
  }

  // The theme AJAX-replaces collection results after filters are applied.
  // Re-create the infinite-scroll observer against the new grid and URL.
  document.addEventListener('collection:reloaded', function () {
    window.requestAnimationFrame(function () {
      init(true);
    });
  });
})();
